// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Solar System page of the headset's side panel (owner,
 * 2026-11-04; docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md): the browser's card and
 * strip (ui/solarSystemMode.ts) for a ray and a trigger.
 *
 * - Over the orrery: the layout (true distances or true sizes), the clock
 *   (Today, play or pause, its speed), a button per body to visit it, and
 *   the way out.
 * - On a body: where you are, back to the orrery, a button per other body
 *   to fly there, Fly or Fade, and the way out.
 * - The first flight on a device asks first: fly, fade there instead, or
 *   not go.
 *
 * Painting only: the state comes from the host, the presses go back as
 * `VrSolarAction`s through the side panel.
 */

import { t, type MessageKey } from '../../i18n'
import { PANEL_COLORS, type PanelPainter } from '../vrPanelKit'
import { SOLAR_BODIES, type SolarBodyId } from './bodies'
import type { OrreryLayout } from './layout'

const W = 1120
const H = 1000
const PAD = 40

export type SolarTravelChoice = 'fly' | 'fade'

export type VrSolarAction =
  | { kind: 'solar-enter' }
  | { kind: 'solar-leave' }
  | { kind: 'solar-overview' }
  | { kind: 'solar-layout'; layout: OrreryLayout }
  | { kind: 'solar-today' }
  | { kind: 'solar-play' }
  | { kind: 'solar-rate' }
  | { kind: 'solar-visit'; body: SolarBodyId }
  | { kind: 'solar-fly'; body: SolarBodyId }
  | { kind: 'solar-travel'; travel: SolarTravelChoice }
  | { kind: 'solar-answer'; choice: SolarTravelChoice | null }

export interface VrSolarState {
  state: 'off' | 'overview' | 'body'
  body: SolarBodyId | null
  layout: OrreryLayout
  playing: boolean
  rateKey: MessageKey
  dateLabel: string
  travel: SolarTravelChoice | null
  /** A flight waits for the comfort answer: where to. */
  asking: SolarBodyId | null
  /** A body's name in the reader's language. */
  names: Readonly<Record<SolarBodyId, string>>
}

const hex = (c: number): string => `#${c.toString(16).padStart(6, '0')}`

function header<A>(p: PanelPainter<A>, close: A): void {
  p.frame(32)
  p.text(t('solar.title'), PAD, 70, { size: 42, weight: 700 })
  p.button(W - PAD - 68, 36, 68, 68, '', close, { icon: 'close', radius: 34 })
}

/** A grid of body buttons, three a row, each with its colour; `act` makes each one's press. */
function bodyGrid<A>(p: PanelPainter<A>, state: VrSolarState, y: number, ids: readonly SolarBodyId[], act: (id: SolarBodyId) => A): number {
  const cols = 3
  const gap = 14
  const w = (W - PAD * 2 - gap * (cols - 1)) / cols
  const h = 76
  ids.forEach((id, i) => {
    const x = PAD + (i % cols) * (w + gap)
    const top = y + Math.floor(i / cols) * (h + gap)
    const colour = SOLAR_BODIES.find((b) => b.id === id)?.colour ?? 0xffffff
    p.box(x, top, w, h, 18, PANEL_COLORS.surface, PANEL_COLORS.border)
    p.g.beginPath()
    p.g.arc(x + 34, top + h / 2, 11, 0, Math.PI * 2)
    p.g.fillStyle = hex(colour)
    p.g.fill()
    p.text(state.names[id], x + 58, top + h / 2, { size: 28, weight: 600, maxWidth: w - 70 })
    p.hit(x, top, w, h, act(id))
  })
  return y + Math.ceil(ids.length / cols) * (h + gap)
}

export function drawSolar<A>(p: PanelPainter<A>, state: VrSolarState | null, wrap: (a: VrSolarAction) => A, close: A): void {
  header(p, close)
  if (!state) return
  const B = (label: string, x: number, y: number, w: number, h: number, action: VrSolarAction, opts: { active?: boolean; primary?: boolean; danger?: boolean } = {}) =>
    p.button(x, y, w, h, label, wrap(action), { size: 28, radius: h / 2, ...opts })

  if (state.asking) {
    p.box(PAD, 140, W - PAD * 2, 470, 24, PANEL_COLORS.surface, PANEL_COLORS.accent)
    p.text(t('solar.comfort.title'), PAD + 36, 200, { size: 38, weight: 700 })
    p.wrap(t('solar.comfort.text'), W - PAD * 2 - 72, 5, 28).forEach((line, i) => p.text(line, PAD + 36, 262 + i * 42, { size: 28, color: PANEL_COLORS.textSecondary }))
    const bw = (W - PAD * 2 - 72 - 24) / 2
    B(t('solar.comfort.fly'), PAD + 36, 500, bw, 84, { kind: 'solar-answer', choice: 'fly' }, { primary: true })
    B(t('solar.comfort.fade'), PAD + 36 + bw + 24, 500, bw, 84, { kind: 'solar-answer', choice: 'fade' })
    B(t('vr.solar.cancel'), PAD, H - 130, W - PAD * 2, 84, { kind: 'solar-answer', choice: null })
    return
  }

  if (state.state === 'off') {
    p.wrap(t('vr.solar.intro'), W - PAD * 2, 5, 30).forEach((line, i) => p.text(line, PAD, 170 + i * 44, { size: 30, color: PANEL_COLORS.textSecondary }))
    B(t('vr.solar.open'), PAD, 420, W - PAD * 2, 96, { kind: 'solar-enter' }, { primary: true })
    return
  }

  if (state.state === 'overview') {
    // The layout.
    const half = (W - PAD * 2 - 16) / 2
    B(t('solar.layout.distances'), PAD, 130, half, 80, { kind: 'solar-layout', layout: 'distances' }, { active: state.layout === 'distances' })
    B(t('solar.layout.sizes'), PAD + half + 16, 130, half, 80, { kind: 'solar-layout', layout: 'sizes' }, { active: state.layout === 'sizes' })
    // The clock.
    p.text(state.dateLabel, PAD, 262, { size: 30, weight: 600 })
    const cw = (W - PAD * 2 - 32) / 3
    B(t('solar.clock.today'), PAD, 292, cw, 76, { kind: 'solar-today' })
    B(t(state.playing ? 'solar.clock.pause' : 'solar.clock.play'), PAD + cw + 16, 292, cw, 76, { kind: 'solar-play' }, { active: state.playing })
    B(t(state.rateKey), PAD + (cw + 16) * 2, 292, cw, 76, { kind: 'solar-rate' })
    // A button per body.
    p.text(t('vr.solar.visit'), PAD, 412, { size: 26, weight: 700, color: PANEL_COLORS.accentText })
    const end = bodyGrid(p, state, 436, SOLAR_BODIES.map((b) => b.id), (id) => wrap({ kind: 'solar-visit', body: id }))
    p.wrap(t(state.layout === 'distances' ? 'vr.solar.hint.distances' : 'vr.solar.hint.sizes'), W - PAD * 2, 2, 24).forEach((line, i) => p.text(line, PAD, end + 16 + i * 32, { size: 24, color: PANEL_COLORS.textSecondary }))
    B(t('vr.solar.leave'), PAD, H - 116, W - PAD * 2, 80, { kind: 'solar-leave' }, { danger: true })
    return
  }

  // On a body.
  const here = state.body ? t('solar.strip.on', { body: state.names[state.body] }) : t('solar.title')
  p.text(here, PAD, 160, { size: 34, weight: 700 })
  B(t('vr.solar.back'), PAD, 196, W - PAD * 2, 80, { kind: 'solar-overview' })
  p.text(t('solar.strip.fly'), PAD, 330, { size: 26, weight: 700, color: PANEL_COLORS.accentText })
  const others = SOLAR_BODIES.map((b) => b.id).filter((id) => id !== state.body)
  const end = bodyGrid(p, state, 354, others, (id) => wrap({ kind: 'solar-fly', body: id }))
  p.text(t('solar.travel.label'), PAD, end + 44, { size: 28, weight: 600 })
  const tw = 220
  B(t('solar.travel.fly'), W - PAD - tw * 2 - 16, end + 4, tw, 76, { kind: 'solar-travel', travel: 'fly' }, { active: state.travel === 'fly' })
  B(t('solar.travel.fade'), W - PAD - tw, end + 4, tw, 76, { kind: 'solar-travel', travel: 'fade' }, { active: state.travel === 'fade' })
  B(t('vr.solar.leave'), PAD, H - 116, W - PAD * 2, 80, { kind: 'solar-leave' }, { danger: true })
}
